import { useState } from 'react'
import { router } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import type { VendorMembership } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  memberships: VendorMembership[]
  current_role: string
}

const roleColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  owner: 'error',
  admin: 'primary',
  member: 'default',
}

export default function MembershipsIndex({ vendor, memberships, current_role }: Props) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('member')
  const isOwner = current_role === 'owner'

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(`/vendors/${vendor.slug}/memberships`, {
      vendor_membership: { email, role },
    })
    setEmail('')
  }

  const handleRoleChange = (membershipId: number, newRole: string) => {
    router.patch(`/vendors/${vendor.slug}/memberships/${membershipId}`, {
      vendor_membership: { role: newRole },
    })
  }

  const handleRemove = (membershipId: number) => {
    if (confirm('Are you sure you want to remove this member?')) {
      router.delete(`/vendors/${vendor.slug}/memberships/${membershipId}`)
    }
  }

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => router.visit(`/vendors/${vendor.slug}`)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5">{vendor.name} — Members</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Invite Member</Typography>
        <Box component="form" onSubmit={handleInvite} sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            label="Email Address"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              {isOwner && <MenuItem value="owner">Owner</MenuItem>}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained">Invite</Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memberships.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.user.email}</TableCell>
                <TableCell>
                  <Chip label={m.role} size="small" color={roleColors[m.role] || 'default'} />
                </TableCell>
                <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <FormControl size="small" sx={{ minWidth: 100, mr: 1 }}>
                    <Select
                      value={m.role}
                      onChange={(e) => handleRoleChange(m.id, e.target.value)}
                      size="small"
                    >
                      <MenuItem value="member">Member</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      {isOwner && <MenuItem value="owner">Owner</MenuItem>}
                    </Select>
                  </FormControl>
                  <IconButton size="small" color="error" onClick={() => handleRemove(m.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </AppLayout>
  )
}
